import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Banner,
  Button,
  ckConfirm,
  Field,
  Icon,
  MenuButton,
  Segmented,
  Select,
  Sheet,
  SimpleTable,
  Spinner,
  Tag,
  TextArea,
  TextInput,
  useCkToast,
  type CkMenuItem,
} from "@NP-Webber/crm-kit/dist/next/index.js";
import { salesApi, type LeadActivity, type LeadDetail, type LeadInput, type LeadStage, type ListenDto } from "../../api/salesApi";
import { useShell } from "../shell/ShellContext";
import { DealDialog, type DealPrefill } from "./DealDialog";
import { addDaysIso, DEAL_STATUS, errorMessage, fmtDate, fmtDateTime, fmtIls, fmtSecs, REGION_LABEL, STAGE_META, todayIso, withEmpty } from "./salesUi";
import { useSalesMeta } from "./useSalesMeta";
import { useYemotCall, YemotSendDialog, type YemotSendKindUi } from "./YemotOutbound";

type LeadDeal = LeadDetail["deals"][number];

const ACTIVITY_ICON: Record<LeadActivity["kind"], string> = { call: "phone", note: "note", status: "flag", listen: "mic", deal: "coin", import: "upload", yemot: "bell" };
const ACTIVITY_LABEL: Record<LeadActivity["kind"], string> = {
  call: "שיחה",
  note: "הערה",
  status: "שינוי סטטוס",
  listen: "הקשבה בקו",
  deal: "עסקה",
  import: "ייבוא",
  yemot: "ימות המשיח",
};

type Details = {
  name: string;
  phone: string;
  source: string;
  interestProductId: string;
  interestText: string;
  statusKey: string;
  assignedRepId: string;
  notes: string;
  details: string;
  followUpOn: string;
};

const toDetails = (l: LeadDetail): Details => ({
  name: l.name ?? "",
  phone: l.phone ?? "",
  source: l.source ?? "",
  interestProductId: l.interestProductId ?? "",
  interestText: l.interestText ?? "",
  statusKey: l.statusKey ?? "",
  assignedRepId: l.assignedRepId ?? "",
  notes: l.notes ?? "",
  details: l.details ?? "",
  followUpOn: l.followUpOn ?? "",
});

/** «תיעוד שיחה» — outcome, note, next call */
function LogBox({ lead, onLogged }: { lead: LeadDetail; onLogged: () => void }) {
  const toast = useCkToast();
  const { optionsOf } = useSalesMeta();
  const [kind, setKind] = useState<"call" | "note">("call");
  const [outcome, setOutcome] = useState("");
  const [body, setBody] = useState("");
  const [next, setNext] = useState<string>(lead.followUpOn ?? "");
  const [busy, setBusy] = useState(false);
  const today = todayIso();
  const quick: Array<{ label: string; value: string }> = [
    { label: "מחר", value: addDaysIso(today, 1) },
    { label: "עוד 3 ימים", value: addDaysIso(today, 3) },
    { label: "שבוע", value: addDaysIso(today, 7) },
    { label: "בלי", value: "" },
  ];

  const submit = async () => {
    if (kind === "note" && !body.trim()) {
      toast.error("כתבו הערה");
      return;
    }
    if (kind === "call" && !outcome && !body.trim()) {
      toast.error("בחרו תוצאה או כתבו מה היה בשיחה");
      return;
    }
    setBusy(true);
    try {
      await salesApi.leads.log(lead.id, {
        kind,
        outcomeKey: kind === "call" ? outcome || null : undefined,
        body: body.trim() || null,
        ...(kind === "call" || next !== (lead.followUpOn ?? "") ? { followUpOn: next || null } : {}),
      });
      toast.success(kind === "call" ? "השיחה תועדה" : "ההערה נשמרה");
      setBody("");
      setOutcome("");
      onLogged();
    } catch (e) {
      toast.error(errorMessage(e, "התיעוד נכשל"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sl-logbox">
      <div className="sl-logbox__title">
        <span>
          <Icon name="phone" size={16} /> תיעוד שיחה
        </span>
        <Segmented
          ariaLabel="סוג"
          value={kind}
          onChange={(v) => setKind(v as "call")}
          items={[
            { value: "call", label: "שיחה" },
            { value: "note", label: "הערה" },
          ]}
        />
      </div>
      {kind === "call" ? (
        <Field label="תוצאה">
          <Select options={withEmpty("— בחירה —", optionsOf("sales_lead_outcome"))} value={outcome} onChange={setOutcome} aria-label="תוצאת השיחה" />
        </Field>
      ) : null}
      <Field label={kind === "call" ? "מה היה בשיחה" : "הערה"} full>
        <TextArea rows={3} value={body} onChange={(e) => setBody(e.target.value)} />
      </Field>
      <Field label="לחזור ב־">
        <div className="sl-row">
          <TextInput type="date" value={next} onChange={(e) => setNext(e.target.value)} style={{ width: 160 }} />
          {quick.map((q) => (
            <Button key={q.label} size="sm" variant={next === q.value ? "primary" : "default"} onClick={() => setNext(q.value)}>
              {q.label}
            </Button>
          ))}
        </div>
      </Field>
      <div className="sl-row sl-row--end">
        <Button variant="primary" icon="check" disabled={busy} onClick={() => void submit()}>
          {busy ? "שומר…" : kind === "call" ? "תיעוד השיחה" : "שמירת ההערה"}
        </Button>
      </div>
    </div>
  );
}

/** The lead card (a side sheet): details, call log, timeline, listens, deals, closing a deal. */
export function LeadSheet({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const toast = useCkToast();
  const navigate = useNavigate();
  const { allowed } = useShell();
  const canEdit = allowed("sales.edit");
  const canManage = allowed("sales.manage");
  const { label, optionsOf, productOptions, repOptions } = useSalesMeta();
  const [lead, setLead] = useState<LeadDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [d, setD] = useState<Details | null>(null);
  const [saving, setSaving] = useState(false);
  const [closing, setClosing] = useState<DealPrefill | null>(null);
  const [yemotSend, setYemotSend] = useState<YemotSendKindUi | null>(null);

  const load = useCallback(() => {
    salesApi.leads
      .get(id)
      .then((l) => {
        setLead(l);
        setD(toDetails(l));
        setError(null);
      })
      .catch((e) => setError(errorMessage(e, "הליד לא נמצא")));
  }, [id]);
  useEffect(() => {
    setLead(null);
    load();
  }, [load]);

  const refresh = () => {
    load();
    onChanged();
  };
  const yemotCall = useYemotCall(refresh);
  const yemotOn = Boolean(yemotCall.setup?.configured && lead?.phone);
  const leadId = lead?.id;
  const leadIds = useMemo(() => (leadId ? [leadId] : []), [leadId]);

  const dirty = Boolean(lead && d && JSON.stringify(toDetails(lead)) !== JSON.stringify(d));
  const set = (k: keyof Details, v: string) => setD((p) => (p ? { ...p, [k]: v } : p));

  const save = async () => {
    if (!lead || !d) return;
    setSaving(true);
    const n = (s: string) => s.trim() || null;
    const body: LeadInput = {
      name: n(d.name),
      phone: n(d.phone),
      source: d.source || null,
      interestProductId: d.interestProductId || null,
      interestText: n(d.interestText),
      statusKey: d.statusKey || null,
      assignedRepId: d.assignedRepId || null,
      notes: n(d.notes),
      details: n(d.details),
      followUpOn: d.followUpOn || null,
    };
    try {
      await salesApi.leads.update(lead.id, body);
      toast.success("הליד נשמר");
      refresh();
    } catch (e) {
      toast.error(errorMessage(e, "השמירה נכשלה"));
    } finally {
      setSaving(false);
    }
  };

  const setStage = async (stage: LeadStage, ok: string) => {
    if (!lead) return;
    try {
      await salesApi.leads.update(lead.id, { stage });
      toast.success(ok);
      refresh();
    } catch (e) {
      toast.error(errorMessage(e, "הפעולה נכשלה"));
    }
  };

  const remove = async () => {
    if (!lead) return;
    if (!(await ckConfirm({ title: "למחוק את הליד?", message: "הליד, היסטוריית השיחות וההקשבות שלו יימחקו. עסקאות שנסגרו יישארו.", confirmLabel: "מחיקה", danger: true }))) return;
    try {
      await salesApi.leads.remove(lead.id);
      toast.success("הליד נמחק");
      onChanged();
      onClose();
    } catch (e) {
      toast.error(errorMessage(e, "המחיקה נכשלה"));
    }
  };

  const open = lead ? lead.stage === "new" || lead.stage === "in_progress" : false;
  const menu: CkMenuItem[] = lead
    ? [
        ...(canEdit
          ? open
            ? [
                { key: "lost", label: "לא רלוונטי", icon: "x", onClick: () => void setStage("lost", "סומן כלא רלוונטי") },
                { key: "done", label: "טופל", icon: "checkAll", onClick: () => void setStage("done", "סומן כטופל") },
              ]
            : [{ key: "reopen", label: "פתיחה מחדש", icon: "refresh", onClick: () => void setStage("in_progress", "הליד נפתח מחדש") }]
          : []),
        ...(canEdit && yemotOn ? [{ key: "yemot-call-phone", label: "חיוג דרך ימות מטלפון אחר", icon: "phone", onClick: () => yemotCall.start(lead, { askPhone: true }) }] : []),
        ...(canManage && yemotOn
          ? [
              { key: "yemot-tzintuk", label: "צינתוק בימות", icon: "bell", onClick: () => setYemotSend("tzintuk") },
              { key: "yemot-voice", label: "הודעה קולית בימות", icon: "mic", onClick: () => setYemotSend("voice") },
            ]
          : []),
        ...(lead.customerId ? [{ key: "customer", label: "לכרטיס הלקוח", icon: "user", onClick: () => navigate(`/customers?card=${lead.customerId}`) }] : []),
        ...(canManage ? [{ divider: true as const }, { key: "del", label: "מחיקת הליד", icon: "trash", danger: true, onClick: () => void remove() }] : []),
      ]
    : [];

  const stage = lead ? STAGE_META[lead.stage] : null;
  return (
    <Sheet open onClose={onClose} width={680} ariaLabel="ליד">
      <div className="sl-sheet">
        <div className="sl-sheet__head">
          <div className="sl-sheet__title">
            <b>{lead ? lead.name || lead.phone || "ליד" : "ליד"}</b>
            {lead ? (
              <span className="sl-row">
                {stage ? <Tag tone={stage.tone}>{stage.label}</Tag> : null}
                {lead.statusKey ? <Tag tone="neutral">{label("sales_lead_status", lead.statusKey)}</Tag> : null}
                {lead.phone && lead.name ? (
                  <a className="sl-ltr" href={`tel:${lead.phoneNormalized ?? lead.phone}`}>
                    {lead.phone}
                  </a>
                ) : null}
              </span>
            ) : null}
          </div>
          <div className="sl-row">
            {lead && canEdit && yemotOn ? (
              <Button size="sm" icon="phone" disabled={yemotCall.busy} onClick={() => yemotCall.start(lead)} title="הטלפון שלך יצלצל, ואז הקו יחייג אל הליד">
                {yemotCall.busy ? "מחייג…" : "חיוג"}
              </Button>
            ) : null}
            {lead && canEdit ? (
              <Button
                size="sm"
                variant="primary"
                icon="coin"
                onClick={() =>
                  setClosing({ leadId: lead.id, name: lead.name, phone: lead.phone, productId: lead.interestProductId, source: lead.source, repId: lead.assignedRepId })
                }
              >
                סגירת עסקה
              </Button>
            ) : null}
            {lead && menu.length ? (
              <MenuButton align="end" width={200} items={menu} renderButton={(p) => <Button {...p} size="sm" variant="ghost" icon="more" iconOnly aria-label="פעולות" />} />
            ) : null}
            <Button size="sm" variant="ghost" icon="x" iconOnly aria-label="סגירה" onClick={onClose} />
          </div>
        </div>
        <div className="sl-sheet__body">
          {error ? <Banner tone="danger">{error}</Banner> : null}
          {!lead && !error ? (
            <div className="sl-busy">
              <Spinner size={24} />
            </div>
          ) : null}
          {lead && d ? (
            <>
              {canEdit ? <LogBox key={lead.updatedAt} lead={lead} onLogged={refresh} /> : null}

              <div className="sl-row sl-row--between">
                <h4 className="sl-section-title">פרטים</h4>
                {canEdit && dirty ? (
                  <div className="sl-row">
                    <Button size="sm" variant="ghost" onClick={() => setD(toDetails(lead))}>
                      ביטול
                    </Button>
                    <Button size="sm" variant="primary" icon="check" disabled={saving} onClick={() => void save()}>
                      {saving ? "שומר…" : "שמירת פרטים"}
                    </Button>
                  </div>
                ) : null}
              </div>
              <fieldset disabled={!canEdit} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
                <div className="sl-grid2">
                  <Field label="שם">
                    <TextInput value={d.name} onChange={(e) => set("name", e.target.value)} />
                  </Field>
                  <Field label="טלפון">
                    <TextInput ltr type="tel" value={d.phone} onChange={(e) => set("phone", e.target.value)} />
                  </Field>
                  <Field label="מקור">
                    <Select options={withEmpty("—", optionsOf("sales_lead_source"))} value={d.source} onChange={(v) => set("source", v)} aria-label="מקור" disabled={!canEdit} />
                  </Field>
                  <Field label="מתעניין ב־">
                    <Select options={withEmpty("—", productOptions)} value={d.interestProductId} onChange={(v) => set("interestProductId", v)} aria-label="מוצר" disabled={!canEdit} />
                  </Field>
                  {!d.interestProductId ? (
                    <Field label="התעניינות (טקסט)">
                      <TextInput value={d.interestText} onChange={(e) => set("interestText", e.target.value)} />
                    </Field>
                  ) : null}
                  <Field label="סטטוס">
                    <Select options={withEmpty("—", optionsOf("sales_lead_status"))} value={d.statusKey} onChange={(v) => set("statusKey", v)} aria-label="סטטוס" disabled={!canEdit} />
                  </Field>
                  <Field label="נציג">
                    <Select options={withEmpty("— ללא —", repOptions)} value={d.assignedRepId} onChange={(v) => set("assignedRepId", v)} aria-label="נציג" disabled={!canEdit} />
                  </Field>
                  <Field label="לחזור ב־">
                    <TextInput type="date" value={d.followUpOn} onChange={(e) => set("followUpOn", e.target.value)} />
                  </Field>
                </div>
                <div className="sl-stack" style={{ marginTop: 12 }}>
                  <Field label="הערות" full>
                    <TextArea rows={2} value={d.notes} onChange={(e) => set("notes", e.target.value)} />
                  </Field>
                  <Field label="פרטים נוספים" full>
                    <TextArea rows={2} value={d.details} onChange={(e) => set("details", e.target.value)} />
                  </Field>
                </div>
              </fieldset>
              <dl className="sl-kv">
                <div>
                  <dt>תאריך פנייה</dt>
                  <dd>{fmtDate(lead.inquiryOn) || "—"}</dd>
                </div>
                <div>
                  <dt>קשר אחרון</dt>
                  <dd>{fmtDate(lead.lastContactOn) || "—"}</dd>
                </div>
                <div>
                  <dt>לא ענה</dt>
                  <dd>{lead.noAnswerCount ? `×${lead.noAnswerCount}` : "—"}</dd>
                </div>
                <div>
                  <dt>אזור</dt>
                  <dd>{REGION_LABEL[lead.region]}</dd>
                </div>
              </dl>

              {lead.deals.length ? (
                <>
                  <h4 className="sl-section-title">עסקאות</h4>
                  <SimpleTable
                    ariaLabel="עסקאות"
                    rows={lead.deals}
                    rowKey="id"
                    onRowClick={(r: LeadDeal) => navigate(`/sales/deals/${r.id}`)}
                    columns={[
                      { key: "dealOn", label: "תאריך", render: (r: LeadDeal) => fmtDate(r.dealOn) },
                      { key: "productName", label: "מוצר" },
                      { key: "total", label: "סכום", align: "end", render: (r: LeadDeal) => <span className="sl-num">{fmtIls(r.totalIlsAgorot)}</span> },
                      { key: "status", label: "סטטוס", render: (r: LeadDeal) => <Tag tone={DEAL_STATUS[r.status].tone}>{DEAL_STATUS[r.status].label}</Tag> },
                    ]}
                  />
                </>
              ) : null}

              {lead.listens.length ? (
                <>
                  <h4 className="sl-section-title">
                    הקשבות בקו ({lead.listens.length}) · הארוכה {fmtSecs(lead.listenSeconds)}
                  </h4>
                  <SimpleTable
                    ariaLabel="הקשבות"
                    rows={lead.listens}
                    rowKey="id"
                    maxHeight={240}
                    columns={[
                      { key: "startedAt", label: "מתי", render: (r: ListenDto) => fmtDateTime(r.startedAt) },
                      { key: "ext", label: "שלוחה", render: (r: ListenDto) => r.extensionLabel || <span className="sl-code">{r.extensionPath}</span> },
                      { key: "seconds", label: "משך", align: "end", render: (r: ListenDto) => <span className="sl-num">{fmtSecs(r.seconds)}</span> },
                    ]}
                  />
                </>
              ) : null}

              <h4 className="sl-section-title">היסטוריה</h4>
              {lead.activities.length ? (
                <ul className="sl-timeline">
                  {lead.activities.map((a) => (
                    <li key={a.id}>
                      <span className="sl-timeline__icon">
                        <Icon name={ACTIVITY_ICON[a.kind] ?? "note"} size={14} />
                      </span>
                      <div className="sl-timeline__body">
                        <span>
                          <b>{ACTIVITY_LABEL[a.kind] ?? a.kind}</b>
                          {a.outcomeKey ? ` · ${label("sales_lead_outcome", a.outcomeKey)}` : ""}
                        </span>
                        {a.body ? <p>{a.body}</p> : null}
                        <span className="sl-timeline__meta">
                          {fmtDateTime(a.occurredAt)}
                          {a.actorName ? ` · ${a.actorName}` : ""}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="sl-muted">עוד אין פעילות</p>
              )}
            </>
          ) : null}
        </div>
      </div>
      <YemotSendDialog kind={yemotSend} ids={leadIds} onClose={() => setYemotSend(null)} onDone={refresh} />
      {yemotCall.dialog}
      <DealDialog
        open={Boolean(closing)}
        prefill={closing}
        onClose={() => setClosing(null)}
        onSaved={() => {
          refresh();
        }}
      />
    </Sheet>
  );
}
